// Angular modules
import { Component }          from '@angular/core';

// Ionic modules
import { GoogleAnalytics }    from '@ionic-native/google-analytics';
import { NavController }      from 'ionic-angular';
import { MenuController }     from 'ionic-angular';
import { ToastController }    from 'ionic-angular';

// External modules
import { Language }           from '../../shared/models/language';
import { ProductFamily }      from '../../shared/models/productFamily';
import { ApplicationService } from '../../shared/providers/application-service';
import { DataService }        from '../../shared/providers/data-service';
import { DocumentService }    from '../../shared/providers/document-service';
import { LangService }        from '../../shared/providers/lang-service';
import { TranslateService }   from '@ngx-translate/core';

// Helpers
import { IndexedDbHelper }    from '../../shared/helpers/indexed-db.helper';
import { StorageHelper }      from '../../shared/helpers/storage.helper';

@Component({
  selector: 'page-synchronize',
  templateUrl: 'synchronize.html',
  providers: [LangService, DataService, ApplicationService]
})

export class SynchronizePage
{
  serverVersion      : string;
  updatedDate        : string;
  currentVersion     : string;
  downloadStateText  : string;
  downloadState      : boolean;
  inProgress         : boolean    = false;
  isCompleted        : boolean    = false;
  user               : any        = null;
  languages          : Language[] = [];
  downStarted        : string     = 'Download Completed';
  downFailed         : string     = 'Download Failed';
  downloading        : string     = 'Downloading ... ';
  unknown            : string     = 'Unknown';

  constructor(
    public  navCtrl           : NavController,
    public  dataService       : DataService,
    public  toastCtrl         : ToastController,
    public  langService       : LangService,
    public  documentService   : DocumentService,
    public  applicationService: ApplicationService,
    public  translate         : TranslateService,
    public  menuCtrl          : MenuController,
    private analytics         : GoogleAnalytics)
  {
    var context = this;

    this.initTranslations();

    context.user = StorageHelper.getUser();
    context.init();
  }

  initTranslations()
  {
    var context = this;
    this.translate.get('DOWNLOAD_COMPLETED').subscribe(
      value => {
        context.downStarted = value;
      })
    this.translate.get('DOWNLOAD_FAILED').subscribe(
      value => {
        context.downFailed = value;
      })
    this.translate.get('DOWNLOADING').subscribe(
      value => {
        context.downloading = value;
      })
    this.translate.get('UNKNOWN').subscribe(
      value => {
        context.unknown = value;
      })
  }

  ionViewDidLoad()
  {
    this.analytics.trackView("Product result");
  }

  presentToast(message)
  {
    let toast = this.toastCtrl.create({
      message: message,
      duration: 3000
    });
    toast.present();
  }

  toggleMenu()
  {
    this.inProgress         = false;
    this.isCompleted        = false;
    this.downloadState      = false;
    this.downloadStateText  = this.downloading;
    this.menuCtrl.toggle();
  }

  async init()
  {
    this.serverVersion    = this.unknown;
    this.currentVersion   = this.unknown;

    this.getCurrentVersion()
    await this.getApplicationInfos();
    this.downloadState      = false;
    this.downloadStateText  = this.downloading;
    this.inProgress         = false;
    this.isCompleted        = false;
  }

  getCurrentVersion()
  {
    const document = StorageHelper.getDocument();
    if(document != null)
      this.currentVersion = document.Version;
  }

  async synchronize()
  {
    var context        = this;
    this.downloadState = true;
    this.inProgress    = true;

    StorageHelper.removeDocument()
    StorageHelper.removeKeyPoints();
    StorageHelper.removeMixtures();
    StorageHelper.removeProducts();
    StorageHelper.removeContacts();
    StorageHelper.removeProductFamilies();
    StorageHelper.removeLanguages();
    StorageHelper.removeCategories();

    IndexedDbHelper.clearImages();
    IndexedDbHelper.clearTranslations();

    this.syncData({
      success: () =>
      {
        context.downloadStateText  = context.downStarted;
        context.inProgress         = false;
        context.isCompleted        = true;
      },
      error: () =>
      {
        context.downloadStateText  = context.downFailed;
        context.inProgress         = false;
        context.isCompleted        = false;
      }
    });
  }

  async syncData(callback: {success, error})
  {
    try
    {
      await this.syncCategories();
      await this.syncProductFamilies();

      // load all images
      await this.syncProductImages();

      // synchronize all data
      await this.syncKeyPoints();
      await this.syncGasMixtures();
      await this.syncLanguages();
      await this.syncProducts();
      await this.syncContacts();

      await this.syncTranslations(this.languages);

      this.currentVersion = this.serverVersion;
      this.saveDocumentInfos(this.serverVersion, this.updatedDate);

      callback.success();
    }
    catch (error)
    {
      console.log('error in syncData', error);
      this.downFailed = error;
      callback.error();
    }
  }

  async syncTranslations(languages: Language[]): Promise<void>
  {
    for (const language of languages)
    {
      const translations = await this.langService.requestLanguageById(language.LanguageId);
      await IndexedDbHelper.setTranslations(language.Code, translations);
    }

    console.log("translations are saved ");
  }

  async getApplicationInfos(): Promise<void>
  {
    try
    {
      const document = await this.documentService.requestDocumentVersion();
      this.serverVersion = document.Name;
      this.updatedDate   = document.Updated;
    } catch (error)
    {
      this.downloadState = true;
      this.presentToast(error);
    }
  }

  async syncCategories()
  {
    const categories = await this.dataService.requestProductCategories()
    StorageHelper.setCategories(categories);
    console.log("categories info are saved ");
  }

  async syncProducts(): Promise<void>
  {
    const products = await this.dataService.requestProducts();
    StorageHelper.setProducts(products);
    console.log("products info are saved ");
  }

  async syncContacts(): Promise<void>
  {
    const contacts = await this.dataService.requestContact();
    StorageHelper.setContacts(contacts);
    console.log("contacts info are saved ");
  }

  async syncProductFamilies(): Promise<ProductFamily[]|null>
  {
    try
    {
      const productFamilies = await this.dataService.requestProductFamilies();
      StorageHelper.setProductFamilies(productFamilies);
      console.log("productFamilies info are saved ");
      return productFamilies;
    }
    catch (error)
    {
      this.presentToast(error);
    }
    return null;
  }

  async syncProductImages(): Promise<void>
  {
    const images = await this.dataService.requestImages();
    if(images == null)
      return;

    for (const image of images)
    {
      const imageWithContent = await this.dataService.requestImageContent(image.ImageId);
      IndexedDbHelper.setImage(image.ProductFamily.trim().toLocaleLowerCase(), imageWithContent);
    }
  }

  async syncKeyPoints()
  {
    const keyPoints = await this.dataService.requestKeyPoints();
    StorageHelper.setKeyPoints(keyPoints);
    console.log("KeyPoints info are saved ");
  }

  async syncGasMixtures()
   {
    const mixtures = await this.dataService.requestGasMixtures();
    StorageHelper.setMixtures(mixtures);
    console.log("Gas Mixtures info are saved ");
   }

   async syncLanguages()
   {
    const languages = await this.langService.requestLanguages();
    this.languages = languages;
    StorageHelper.setLanguages(languages);
    console.log("Languages info are saved ");
  }

  saveDocumentInfos(currentVersion, updated): void
  {
    StorageHelper.setDocument({
      Version: currentVersion,
      Updated: updated,
    });

    console.log("document info are saved : version " + currentVersion + " date " + updated);
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/synchronize/synchronize.ts